import { useRef, useState } from "react";
import { discardUpload, uploadImage } from "../api/uploads.js";

/**
 * Image upload field (US-D2/G4). On create the image is required; on edit it is optional —
 * leaving it unchanged keeps the existing stored name (US-E2). Only an image uploaded in
 * THIS session is discarded on replace (we never delete the ad's previously-saved image).
 */
export function ImageUpload({
  value,
  existingName,
  onChange,
}: {
  /** The current stored filename committed to the form (storedName), if any. */
  value?: string;
  /** The ad's already-saved image (edit) — shown as the current image, never auto-discarded. */
  existingName?: string;
  onChange: (storedName: string | undefined) => void;
}) {
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [preview, setPreview] = useState<string | null>(null);
  const ownedTmpRef = useRef<string | null>(null); // uploaded this session → safe to discard

  async function onPick(e: React.ChangeEvent<HTMLInputElement>) {
    const file = e.target.files?.[0];
    if (!file) return;
    setError(null);
    setBusy(true);
    try {
      if (ownedTmpRef.current) {
        await discardUpload(ownedTmpRef.current).catch(() => undefined);
        ownedTmpRef.current = null;
      }
      const { storedName } = await uploadImage(file);
      ownedTmpRef.current = storedName;
      onChange(storedName); // commit the value first — preview is cosmetic
      if (typeof URL.createObjectURL === "function") setPreview(URL.createObjectURL(file));
    } catch (err) {
      setError(err instanceof Error ? err.message : "upload failed");
    } finally {
      setBusy(false);
    }
  }

  const committed = value ?? existingName;
  return (
    <div className="image-upload">
      {preview ? (
        <img className="image-preview" src={preview} alt="Selected ad preview" />
      ) : committed ? (
        <p className="muted-note">Current image: {committed}</p>
      ) : (
        <p className="muted-note">No image selected.</p>
      )}
      <input type="file" accept="image/png,image/jpeg,image/gif" onChange={onPick} disabled={busy} aria-label="Ad image" />
      {busy && <span className="muted-note"> Uploading…</span>}
      {error && (
        <span className="field-error" role="alert">
          {error}
        </span>
      )}
    </div>
  );
}
